import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { AmplifierData, AmplifierControlData } from './amplifier';
import { amplifierMqttService } from './AmplifierMqttService';
import { useI18n } from '../../i18n/useI18n';




interface TopBarBottomProps {
    deviceId?: string;
    amplifierData?: AmplifierData | null;
    antennaMode?: 'manual' | 'auto';
}

/**
 * ---------- 天线部分逻辑说明 ----------
 *
 * 一、数据来源与同步
 * - inputAntenna、outputAntenna 以 amplifierData 为准（设备状态），由父组件通过 props 传入。
 * - amplifierData 变化时，useEffect 会把 inputAntenna、outputAntenna 同步到本地 amplifierControlData，
 *   并用于 handleParameterChange 下发指令。
 *
 * 二、输入天线（inputAntenna）
 * - 取值：1 = 输入天线1，2 = 输入天线2。（0 表示未选，路径不绘制）
 * - UI：左侧两个按钮「输入天线1」「输入天线2」，点击即下发；与触发模式双向绑定：天线1↔PTT1(0)，天线2↔PP2(1)。
 * - 路径：接收模式(ptt=0) 时路径终点由 inputAntenna 决定（1→天线1 端口，2→天线2 端口）；
 *        发射模式(ptt=1|2|3) 时路径起点由 inputAntenna 决定（1→从天线1 经衰减器到主管道，2→从天线2 到主管道）。
 *
 * 三、输出天线（outputAntenna）
 * - 取值：1/2/3/4 = 输出天线1～4。
 * - UI：右侧四个按钮「输出天线1」～「输出天线4」，是否可点由 antennaMode 决定。
 * - antennaMode：由父组件传入。'manual' = 可点击切换输出天线；'auto' = 不可点击（cursor: not-allowed，opacity 0.6）。
 * - 路径：接收模式(ptt=0) 时路径起点由 outputAntenna 决定（1～4 对应右侧四个天线端口）；
 *        发射模式(ptt=1|2|3) 时路径终点由 outputAntenna 决定。
 *
 * 四、信号路径与天线
 * - ptt=0（接收）：generateReceivePath，路径 输出天线 → 主管道 → 输入天线，需 outputAntenna 与 inputAntenna 均有值才绘制。
 * - ptt=1|2|3（发射）：generateTransmitPath，路径 输入天线 → 主管道 → 输出天线，同样需两者均有值才绘制。
 * - generateSignalPath 根据 ptt 在二者间切换，路径用于 SVG 虚线动画。
 *
 * ---------- 天调按钮与功放按钮逻辑说明 ----------
 *
 * 五、功放按钮（power）
 * - 取值：0 = 关闭，1 = 开启。
 * - 数据：来自 amplifierData.power，同步到 amplifierControlData；点击后通过 handleParameterChange({ power }) 下发。
 * - UI：点击在 0/1 间切换；power===1 时按钮为橙色(#cc6600)，否则灰色(#333333)。
 * - 路径影响：
 *   - 接收路径：主管道3→2 段，power===1 走功放节点(ampRight6…1)，否则走衰减器(attenuator7→6)。
 *   - 发射路径：输入天线1→主管道1 段，power===1 穿过衰减器(attenuator1…5)，power===0 绕过衰减器(直连 mainPipe1)；
 *   - 发射路径：主管道2→3 段，power===1 走功放节点(ampRight1…6)，否则走衰减器(attenuator6→7)。
 * - 联动：输入衰减器可随时点击设置（0-3 循环），不依赖功放开关。
 *
 * 六、天调按钮（tuneMode）
 * - 取值：0 = 关闭，1 = 开启，2 = 调谐中，3 = 调谐失败。点击按 0→1→2→3→0 循环。
 * - 数据：来自 amplifierData.tuneMode，同步到 amplifierControlData；点击后 handleParameterChange({ tuneMode: newTuneMode }) 下发。
 * - UI：tuneMode 为 1 或 2 时按钮为蓝色(#0066cc)，否则灰色；按钮下方文案显示「关闭/开启/调谐中/调谐失败」。
 * - 路径影响：无；信号路径始终不经过天调，主管道4↔5 段固定走衰减器(attenuator8↔9)。
 */


export const TopBarBottom: React.FC<TopBarBottomProps> = ({
    deviceId = '1f75d96b40e8ed8e',
    amplifierData,
    antennaMode = 'manual'
}) => {
    const { t } = useI18n();
    const showGreenNodes = false;

    // 定义 AmplifierControlData 状态，用于存储需要下发的控制数据
    // 触发模式 triggerMode：0=PTT1(默认) 1=PP2 3=输入功率触发（与输入天线独立）
    const [amplifierControlData, setAmplifierControlData] = useState<AmplifierControlData>({
        frequency: 0,
        triggerMode: 0,
        tuneMode: 0,
        power: 0,
        inputAttenuator: 0,
        inputAntenna: 0,
        outputAntenna: 0,
        alarmWord: 0
    });

    // 确保deviceId存在
    useEffect(() => {
        console.log('TopBarBottom组件 - 接收到的设备ID:', deviceId);
        if (!deviceId) {
            console.warn('TopBarBottom组件 - 设备ID为空，可能导致部分功能无法正常工作');
        }
    }, [deviceId]);

    // 处理参数更新函数
    const handleParameterChange = async (newData: Partial<AmplifierControlData>) => {
        try {
            const updatedData = { ...amplifierControlData, ...newData };
            setAmplifierControlData(updatedData);
            await amplifierMqttService.sendAmpConfigCommand(deviceId, updatedData);
            console.log('TopBarBottom: 功放控制指令发送成功:', updatedData);
        } catch (error) {
            console.error('TopBarBottom: 功放控制指令发送失败:', error);
            // 可以添加错误提示
        }
    };

    // 当 amplifierData 变化时，更新 AmplifierControlData
    useEffect(() => {
        if (amplifierData) {
            // 更新 AmplifierControlData
            const controlData: AmplifierControlData = {
                frequency: amplifierData.frequency,
                triggerMode: amplifierData.triggerMode,
                tuneMode: amplifierData.tuneMode,
                power: amplifierData.power,
                inputAttenuator: amplifierData.inputAttenuator,
                inputAntenna: amplifierData.inputAntenna,
                outputAntenna: amplifierData.outputAntenna,
                alarmWord: amplifierData.alarmWord
            };
            setAmplifierControlData(controlData);
        }
    }, [amplifierData]);

    /** 整体下移偏移（仅节点、按钮与文字，不移动整页） */
    const Y_OFFSET = 20;

    // 节点配置
    const pipePathConfig = useMemo(() => ({
        // 左侧输入天线
        start1: { x: 90, y: 57 + Y_OFFSET },
        start2: { x: 90, y: 167 + Y_OFFSET },
        // 衰减器
        attenuatorHead1: { x: 115, y: 15 + Y_OFFSET },
        attenuatorHead2: { x: 265, y: 15 + Y_OFFSET },
        attenuatorHead3: { x: 350, y: 15 + Y_OFFSET },
        attenuatorHead4: { x: 380, y: 15 + Y_OFFSET },
        attenuator1: { x: 115, y: 57 + Y_OFFSET },
        attenuator2: { x: 140, y: 57 + Y_OFFSET },
        attenuator3: { x: 240, y: 57 + Y_OFFSET },
        attenuator4: { x: 265, y: 57 + Y_OFFSET },
        attenuator5: { x: 290, y: 57 + Y_OFFSET },
        attenuator6: { x: 350, y: 57 + Y_OFFSET },
        attenuator7: { x: 620, y: 57 + Y_OFFSET },
        attenuator8: { x: 660, y: 57 + Y_OFFSET },
        attenuator9: { x: 820, y: 57 + Y_OFFSET },
        startRight1: { x: 290, y: 167 + Y_OFFSET },
        // 主管道节点
        mainPipe1: { x: 290, y: 120 + Y_OFFSET },
        mainPipe2: { x: 350, y: 120 + Y_OFFSET },
        mainPipe3: { x: 620, y: 120 + Y_OFFSET },
        mainPipe4: { x: 660, y: 120 + Y_OFFSET },
        mainPipe5: { x: 820, y: 120 + Y_OFFSET },
        mainPipe6: { x: 940, y: 120 + Y_OFFSET },
        //功放按钮附近节点9个
        ampRight1: { x: 350, y: 190 + Y_OFFSET },
        ampRight2: { x: 380, y: 190 + Y_OFFSET },
        ampRight3: { x: 480, y: 190 + Y_OFFSET },
        ampRight4: { x: 500, y: 190 + Y_OFFSET },
        ampRight5: { x: 600, y: 190 + Y_OFFSET },
        ampRight6: { x: 620, y: 190 + Y_OFFSET },
        ampRight7: { x: 660, y: 190 + Y_OFFSET },
        ampRight8: { x: 680, y: 190 + Y_OFFSET },
        ampRight9: { x: 780, y: 190 + Y_OFFSET },
        ampRight10: { x: 820, y: 190 + Y_OFFSET },
        // 右侧输出天线
        rightAntenna1: { x: 1060, y: 35 + Y_OFFSET },
        rightAntenna2: { x: 1060, y: 100 + Y_OFFSET },
        rightAntenna3: { x: 1060, y: 165 + Y_OFFSET },
        rightAntenna4: { x: 1060, y: 230 + Y_OFFSET },
        rightAntennaFinal1: { x: 940, y: 35 + Y_OFFSET },
        rightAntennaFinal2: { x: 940, y: 100 + Y_OFFSET },
        rightAntennaFinal3: { x: 940, y: 165 + Y_OFFSET },
        rightAntennaFinal4: { x: 940, y: 230 + Y_OFFSET }
    }), []);

    // 添加动画样式
    useEffect(() => {
        const updateAnimationStyle = () => {
            const styleId = 'energy-flow-animation';
            const existingStyle = document.getElementById(styleId);

            if (existingStyle) {
                existingStyle.remove();
            }

            const style = document.createElement('style');
            style.id = styleId;

            // 同时定义两个动画，确保无论触发模式如何都能找到对应的动画
            style.textContent = `
                @keyframes flowLeftToRight {
                    0% { stroke-dashoffset: 40; }
                    100% { stroke-dashoffset: 0; }
                }
                @keyframes flowRightToLeft {
                    0% { stroke-dashoffset: 0; }
                    100% { stroke-dashoffset: 40; }
                }
            `;

            document.head.appendChild(style);
        };

        updateAnimationStyle();

        // 清理函数
        return () => {
            const styleId = 'energy-flow-animation';
            const existingStyle = document.getElementById(styleId);
            if (existingStyle) {
                existingStyle.remove();
            }
        };
    }, []);

    // 生成接收模式信号路径（ptt=0：输出天线向输入天线发射信号）
    const generateReceivePath = useCallback(() => {
        if (!amplifierData || amplifierData.ptt !== 0 || !amplifierData.outputAntenna || !amplifierData.inputAntenna) {
            return '';
        }

        let path = '';

        // 1. 从输出天线到主管道6
        switch (amplifierData.outputAntenna) {
            case 1:
                path += `M ${pipePathConfig.rightAntenna1.x} ${pipePathConfig.rightAntenna1.y} L ${pipePathConfig.rightAntennaFinal1.x} ${pipePathConfig.rightAntennaFinal1.y} L ${pipePathConfig.mainPipe6.x} ${pipePathConfig.mainPipe6.y} `;
                break;
            case 2:
                path += `M ${pipePathConfig.rightAntenna2.x} ${pipePathConfig.rightAntenna2.y} L ${pipePathConfig.rightAntennaFinal2.x} ${pipePathConfig.rightAntennaFinal2.y} L ${pipePathConfig.mainPipe6.x} ${pipePathConfig.mainPipe6.y} `;
                break;
            case 3:
                path += `M ${pipePathConfig.rightAntenna3.x} ${pipePathConfig.rightAntenna3.y} L ${pipePathConfig.rightAntennaFinal3.x} ${pipePathConfig.rightAntennaFinal3.y} L ${pipePathConfig.mainPipe6.x} ${pipePathConfig.mainPipe6.y} `;
                break;
            case 4:
                path += `M ${pipePathConfig.rightAntenna4.x} ${pipePathConfig.rightAntenna4.y} L ${pipePathConfig.rightAntennaFinal4.x} ${pipePathConfig.rightAntennaFinal4.y} L ${pipePathConfig.mainPipe6.x} ${pipePathConfig.mainPipe6.y} `;
                break;
        }

        // 2. 主管道6到主管道5
        path += `L ${pipePathConfig.mainPipe5.x} ${pipePathConfig.mainPipe5.y} `;

        // 3. 主管道5到主管道4（路径不经过天调）
        path += `L ${pipePathConfig.attenuator9.x} ${pipePathConfig.attenuator9.y} L ${pipePathConfig.attenuator8.x} ${pipePathConfig.attenuator8.y} L ${pipePathConfig.mainPipe4.x} ${pipePathConfig.mainPipe4.y} `;

        // 4. 主管道4到主管道3
        path += `L ${pipePathConfig.mainPipe3.x} ${pipePathConfig.mainPipe3.y} `;

        // 5. 主管道3到主管道2

        path += `L ${pipePathConfig.attenuator7.x} ${pipePathConfig.attenuator7.y} L ${pipePathConfig.attenuator6.x} ${pipePathConfig.attenuator6.y} L ${pipePathConfig.mainPipe2.x} ${pipePathConfig.mainPipe2.y} `;


        // 6. 主管道2到主管道1
        path += `L ${pipePathConfig.mainPipe1.x} ${pipePathConfig.mainPipe1.y} `;

        // 7. 主管道1到输入天线（按 inputAntenna：1→天线1，2→天线2）
        if (amplifierData.inputAntenna === 1) {
            path += `L ${pipePathConfig.attenuator5.x} ${pipePathConfig.attenuator5.y} L ${pipePathConfig.attenuator4.x} ${pipePathConfig.attenuator4.y} L ${pipePathConfig.attenuatorHead2.x} ${pipePathConfig.attenuatorHead2.y} L ${pipePathConfig.attenuatorHead1.x} ${pipePathConfig.attenuatorHead1.y} L ${pipePathConfig.attenuator1.x} ${pipePathConfig.attenuator1.y} L ${pipePathConfig.start1.x} ${pipePathConfig.start1.y} `;
        } else {
            path += `L ${pipePathConfig.startRight1.x} ${pipePathConfig.startRight1.y} L ${pipePathConfig.start2.x} ${pipePathConfig.start2.y} `;
        }

        return path;
    }, [amplifierData, pipePathConfig]);

    // 生成发射模式信号路径（ptt=1|2|3：输入天线向输出天线发射信号）
    const generateTransmitPath = useCallback(() => {
        if (!amplifierData || !(amplifierData.ptt === 1 || amplifierData.ptt === 2 || amplifierData.ptt === 3) || !amplifierData.outputAntenna || !amplifierData.inputAntenna) {
            return '';
        }

        let path = '';

        // 1. 从输入天线到主管道1
        if (amplifierData.inputAntenna === 1) {
            // 输入天线1：功放=1 穿过当前衰减器，功放=0 绕过当前衰减器
            if (amplifierData.power === 1) {
                path += `M ${pipePathConfig.start1.x} ${pipePathConfig.start1.y} L ${pipePathConfig.attenuator1.x} ${pipePathConfig.attenuator1.y} L ${pipePathConfig.attenuator2.x} ${pipePathConfig.attenuator2.y}`;
                path += ` L ${pipePathConfig.attenuator3.x} ${pipePathConfig.attenuator3.y} L ${pipePathConfig.attenuator4.x} ${pipePathConfig.attenuator4.y} L ${pipePathConfig.attenuator5.x} ${pipePathConfig.attenuator5.y} L ${pipePathConfig.mainPipe1.x} ${pipePathConfig.mainPipe1.y} `;
            } else {
                path += `M ${pipePathConfig.start1.x} ${pipePathConfig.start1.y} L ${pipePathConfig.attenuator1.x} ${pipePathConfig.attenuator1.y} L ${pipePathConfig.attenuatorHead1.x} ${pipePathConfig.attenuatorHead1.y} L ${pipePathConfig.attenuatorHead2.x} ${pipePathConfig.attenuatorHead2.y}
                L ${pipePathConfig.attenuator4.x} ${pipePathConfig.attenuator4.y} L ${pipePathConfig.attenuator5.x} ${pipePathConfig.attenuator5.y} L ${pipePathConfig.mainPipe1.x} ${pipePathConfig.mainPipe1.y} `;
            }
        } else {
            // 输入天线2
            path += `M ${pipePathConfig.start2.x} ${pipePathConfig.start2.y} L ${pipePathConfig.startRight1.x} ${pipePathConfig.startRight1.y} L ${pipePathConfig.mainPipe1.x} ${pipePathConfig.mainPipe1.y} `;
        }

        // 2. 主管道1到主管道2
        path += `L ${pipePathConfig.mainPipe2.x} ${pipePathConfig.mainPipe2.y} `;

        // 3. 主管道2到主管道3（根据功放状态）
        if (amplifierData.power === 1) {
            // 功放开启
            path += `L ${pipePathConfig.ampRight1.x} ${pipePathConfig.ampRight1.y} L ${pipePathConfig.ampRight2.x} ${pipePathConfig.ampRight2.y} L ${pipePathConfig.ampRight3.x} ${pipePathConfig.ampRight3.y} L ${pipePathConfig.ampRight4.x} ${pipePathConfig.ampRight4.y} L ${pipePathConfig.ampRight5.x} ${pipePathConfig.ampRight5.y} L ${pipePathConfig.ampRight6.x} ${pipePathConfig.ampRight6.y} L ${pipePathConfig.mainPipe3.x} ${pipePathConfig.mainPipe3.y} `;
        } else {
            // 功放关闭
            path += `L ${pipePathConfig.attenuator6.x} ${pipePathConfig.attenuator6.y} L ${pipePathConfig.attenuator7.x} ${pipePathConfig.attenuator7.y} L ${pipePathConfig.mainPipe3.x} ${pipePathConfig.mainPipe3.y} `;
        }

        // 4. 主管道3到主管道4
        path += `L ${pipePathConfig.mainPipe4.x} ${pipePathConfig.mainPipe4.y} `;

          // 5. 主管道4到主管道5（根据天调模式）
          if (amplifierData.tuneMode === 0) {
            // 天调关闭
            path += `L ${pipePathConfig.attenuator8.x} ${pipePathConfig.attenuator8.y} L ${pipePathConfig.attenuator9.x} ${pipePathConfig.attenuator9.y} L ${pipePathConfig.mainPipe5.x} ${pipePathConfig.mainPipe5.y} `;
        } else {
            // 天调开启或调谐中或调谐失败
            path += `L ${pipePathConfig.ampRight7.x} ${pipePathConfig.ampRight7.y} L ${pipePathConfig.ampRight8.x} ${pipePathConfig.ampRight8.y} L ${pipePathConfig.ampRight9.x} ${pipePathConfig.ampRight9.y} L ${pipePathConfig.ampRight10.x} ${pipePathConfig.ampRight10.y} L ${pipePathConfig.mainPipe5.x} ${pipePathConfig.mainPipe5.y} `;
        }

        // 6. 主管道5到主管道6
        path += `L ${pipePathConfig.mainPipe6.x} ${pipePathConfig.mainPipe6.y} `;

        // 7. 主管道6到输出天线
        switch (amplifierData.outputAntenna) {
            case 1:
                path += `L ${pipePathConfig.rightAntennaFinal1.x} ${pipePathConfig.rightAntennaFinal1.y} L ${pipePathConfig.rightAntenna1.x} ${pipePathConfig.rightAntenna1.y} `;
                break;
            case 2:
                path += `L ${pipePathConfig.rightAntennaFinal2.x} ${pipePathConfig.rightAntennaFinal2.y} L ${pipePathConfig.rightAntenna2.x} ${pipePathConfig.rightAntenna2.y} `;
                break;
            case 3:
                path += `L ${pipePathConfig.rightAntennaFinal3.x} ${pipePathConfig.rightAntennaFinal3.y} L ${pipePathConfig.rightAntenna3.x} ${pipePathConfig.rightAntenna3.y} `;
                break;
            case 4:
                path += `L ${pipePathConfig.rightAntennaFinal4.x} ${pipePathConfig.rightAntennaFinal4.y} L ${pipePathConfig.rightAntenna4.x} ${pipePathConfig.rightAntenna4.y} `;
                break;
        }

        return path;
    }, [amplifierData, pipePathConfig]);

    // 根据 ptt 生成信号路径：ptt=0 输出→输入，ptt=1|2|3 输入→输出
    const generateSignalPath = useMemo(() => {
        if (!amplifierData) {
            return '';
        }
        if (amplifierData.ptt === 0) {
            return generateReceivePath();
        } else if (amplifierData.ptt === 1 || amplifierData.ptt === 2 || amplifierData.ptt === 3) {
            return generateTransmitPath();
        }
        return '';
    }, [amplifierData, generateReceivePath, generateTransmitPath]);

    return (
        <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #333',
            padding: 'var(--spacing-lg)',
            paddingLeft: '0',
            marginTop: 'var(--spacing-sm)',
            marginLeft: 'var(--spacing-lg)',
            marginRight: 'var(--spacing-lg)',
            marginBottom: 'var(--spacing-lg)'
        }}>
            <svg width="100%" height={270 + Y_OFFSET} viewBox={`0 0 1250 ${270 + Y_OFFSET}`}>

                {/* 信号路径 - 绘制在按钮之前，这样按钮会遮挡穿过的信号 */}
                {/* ptt=0：输出天线向输入天线发射信号，路径右→左，动画从右往左 */}
                {amplifierData?.ptt === 0 && (
                    <path
                        d={generateSignalPath}
                        stroke="#00ff00"
                        strokeWidth="4"
                        fill="none"
                        strokeDasharray="10,10"
                        style={{ animation: 'flowLeftToRight 1.5s linear infinite' }}
                    />
                )}

                {/* ptt=1|2|3：输入天线向输出天线发射信号，路径左→右，动画从左往右 */}
                {(amplifierData?.ptt === 1 || amplifierData?.ptt === 2 || amplifierData?.ptt === 3) && (
                    <path
                        d={generateSignalPath}
                        stroke="#00ff00"
                        strokeWidth="4"
                        fill="none"
                        strokeDasharray="10,10"
                        style={{ animation: 'flowLeftToRight 1.5s linear infinite' }}
                    />
                )}

                {showGreenNodes && (
                    <>
                        {/* 节点标记 - 绘制在按钮之后，确保可见 */}
                        {/* 左侧输入天线节点 */}
                        <circle cx={pipePathConfig.start1.x} cy={pipePathConfig.start1.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.start2.x} cy={pipePathConfig.start2.y} r="4" fill="#00ff00" />
                        {/* 衰减器节点 */}
                        <circle cx={pipePathConfig.attenuatorHead1.x} cy={pipePathConfig.attenuatorHead1.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.attenuatorHead2.x} cy={pipePathConfig.attenuatorHead2.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.attenuatorHead3.x} cy={pipePathConfig.attenuatorHead3.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.attenuatorHead4.x} cy={pipePathConfig.attenuatorHead4.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.attenuator1.x} cy={pipePathConfig.attenuator1.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.attenuator2.x} cy={pipePathConfig.attenuator2.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.attenuator3.x} cy={pipePathConfig.attenuator3.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.attenuator4.x} cy={pipePathConfig.attenuator4.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.attenuator5.x} cy={pipePathConfig.attenuator5.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.attenuator6.x} cy={pipePathConfig.attenuator6.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.attenuator7.x} cy={pipePathConfig.attenuator7.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.attenuator8.x} cy={pipePathConfig.attenuator8.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.attenuator9.x} cy={pipePathConfig.attenuator9.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.startRight1.x} cy={pipePathConfig.startRight1.y} r="4" fill="#00ff00" />

                        {/* 主管道节点 */}
                        <circle cx={pipePathConfig.mainPipe1.x} cy={pipePathConfig.mainPipe1.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.mainPipe2.x} cy={pipePathConfig.mainPipe2.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.mainPipe3.x} cy={pipePathConfig.mainPipe3.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.mainPipe4.x} cy={pipePathConfig.mainPipe4.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.mainPipe5.x} cy={pipePathConfig.mainPipe5.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.mainPipe6.x} cy={pipePathConfig.mainPipe6.y} r="4" fill="#00ff00" />
                        {/* 功放按钮附近节点9个 */}
                        <circle cx={pipePathConfig.ampRight1.x} cy={pipePathConfig.ampRight1.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.ampRight2.x} cy={pipePathConfig.ampRight2.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.ampRight3.x} cy={pipePathConfig.ampRight3.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.ampRight4.x} cy={pipePathConfig.ampRight4.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.ampRight5.x} cy={pipePathConfig.ampRight5.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.ampRight6.x} cy={pipePathConfig.ampRight6.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.ampRight7.x} cy={pipePathConfig.ampRight7.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.ampRight8.x} cy={pipePathConfig.ampRight8.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.ampRight9.x} cy={pipePathConfig.ampRight9.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.ampRight10.x} cy={pipePathConfig.ampRight10.y} r="4" fill="#00ff00" />
                        {/* 右侧输出天线节点 */}
                        <circle cx={pipePathConfig.rightAntenna1.x} cy={pipePathConfig.rightAntenna1.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.rightAntenna2.x} cy={pipePathConfig.rightAntenna2.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.rightAntenna3.x} cy={pipePathConfig.rightAntenna3.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.rightAntenna4.x} cy={pipePathConfig.rightAntenna4.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.rightAntennaFinal1.x} cy={pipePathConfig.rightAntennaFinal1.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.rightAntennaFinal2.x} cy={pipePathConfig.rightAntennaFinal2.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.rightAntennaFinal3.x} cy={pipePathConfig.rightAntennaFinal3.y} r="4" fill="#00ff00" />
                        <circle cx={pipePathConfig.rightAntennaFinal4.x} cy={pipePathConfig.rightAntennaFinal4.y} r="4" fill="#00ff00" />
                    </>
                )}

                {/* 按钮绘制 - 绘制在信号路径之上，遮挡穿过的信号 */}
                {/* 假负载按钮：与功放、输入衰减器同一竖排，大小同功放 100x40 */}
                <g style={{ cursor: 'pointer' }} onClick={() => { /* 假负载点击可在此扩展 */ }}>
                    <rect x="380" y={Y_OFFSET-5} width="100" height="40" fill="#333333" rx="4" ry="4" stroke="#555555" strokeWidth="1" />
                    <text x="425" y={ Y_OFFSET + 20} fill="white" fontSize="16" textAnchor="middle">假负载</text>
                </g>

                {/* 输入天线1：与触发模式 PTT1 双向绑定，点击同时设为 inputAntenna=1、triggerMode=0 */}
                <g style={{ cursor: 'pointer' }} onClick={() => handleParameterChange({ inputAntenna: 1, triggerMode: 0 })}>
                    <rect
                        x="0" y={40 + Y_OFFSET} width="90" height="35"
                        fill={amplifierData?.inputAntenna === 1 ? 'green' : '#333333'}
                        rx="4" ry="4" stroke="#555555" strokeWidth="1"
                    />
                    <text x="45" y={62 + Y_OFFSET} fill="white" fontSize="16" textAnchor="middle" dominantBaseline="middle">{t('输入天线1')}</text>
                </g>

                {/* 输入天线衰减器按钮 */}
                <g>
                    <rect x="140" y={40 + Y_OFFSET} width="100" height="45" fill="#333333" rx="4" ry="4" stroke="#555555" strokeWidth="1" />
                    <text x="190" y={55 + Y_OFFSET} fill="white" fontSize="14" textAnchor="middle">{t('输入天线衰减器')}</text>
                    <text x="190" y={70 + Y_OFFSET} fill="#ffcc00" fontSize="14" textAnchor="middle">6.0DB</text>
                </g>

                {/* 输入天线2：与触发模式 PP2 双向绑定，点击同时设为 inputAntenna=2、triggerMode=1 */}
                <g style={{ cursor: 'pointer' }} onClick={() => handleParameterChange({ inputAntenna: 2, triggerMode: 1 })}>
                    <rect
                        x="0" y={150 + Y_OFFSET} width="90" height="35"
                        fill={amplifierData?.inputAntenna === 2 ? 'green' : '#333333'}
                        rx="4" ry="4" stroke="#555555" strokeWidth="1"
                    />
                    <text x="45" y={172 + Y_OFFSET} fill="white" fontSize="16" textAnchor="middle" dominantBaseline="middle">{t('输入天线2')}</text>
                </g>

                {/* 输入衰减器按钮：颜色随功放开关（开=橙/关=灰），可随时点击设置 DB；显示 0→0DB, 1→1DB, 2→3DB, 3→4DB */}
                <g style={{ cursor: 'pointer' }} onClick={() => {
                    const newInputAttenuator = (amplifierControlData.inputAttenuator + 1) % 4;
                    handleParameterChange({ inputAttenuator: newInputAttenuator });
                }}>
                    <rect x="380" y={170 + Y_OFFSET} width="100" height="40" fill={amplifierData?.power === 1 ? "#cc6600" : "#333333"} rx="4" ry="4" stroke="#555555" strokeWidth="1" />
                    <text x="430" y={188 + Y_OFFSET} fill="white" fontSize="16" textAnchor="middle">{t('输入衰减器')}</text>
                    <text x="430" y={202 + Y_OFFSET} fill="white" fontSize="14" textAnchor="middle">{[0, 1, 3, 4][Number(amplifierData?.inputAttenuator)] ?? 0}DB</text>
                </g>

                {/* 功放按钮 */}
                <g style={{ cursor: 'pointer' }} onClick={() => {
                    // 功放点击事件，切换power值（0和1之间）
                    console.log('TopBarBottom - 点击功放按钮');
                    console.info('TopBarBottom - 当前power值:', amplifierControlData.power);
                    const newPower = amplifierControlData.power === 0 ? 1 : 0;
                    handleParameterChange({ power: newPower });
                }}>
                    <rect x="500" y={170 + Y_OFFSET} width="100" height="40" fill={amplifierData?.power === 1 ? "#cc6600" : "#333333"} rx="4" ry="4" stroke="#555555" strokeWidth="1" />
                    <text x="550" y={190 + Y_OFFSET} fill="white" fontSize="16" textAnchor="middle">{t('功放')}</text>
                </g>

                {/* 天调按钮 */}
                <g style={{ cursor: 'pointer' }} onClick={() => {
                    // 天调点击事件，循环切换tuneMode值（0-3）
                    const newTuneMode = (amplifierControlData.tuneMode + 1) % 4;
                    handleParameterChange({ tuneMode: newTuneMode });
                }}>
                    <rect x="680" y={170 + Y_OFFSET} width="100" height="40" fill={amplifierData?.tuneMode === 1 || amplifierData?.tuneMode === 2 || amplifierData?.tuneMode === 3 ? "#0066cc" : "#333333"} rx="4" ry="4" stroke="#555555" strokeWidth="1" />
                    <text x="730" y={188 + Y_OFFSET} fill="white" fontSize="16" textAnchor="middle">{t('天调')}</text>
                    <text x="730" y={202 + Y_OFFSET} fill="white" fontSize="14" textAnchor="middle">
                        {(() => {
                            switch (amplifierData?.tuneMode) {
                                case 0:
                                    return t('关闭');
                                case 1:
                                    return t('开启');
                                case 2:
                                    return t('调谐中');
                                case 3:
                                    return t('调谐失败');
                                default:
                                    return '未知';
                            }
                        })()}
                    </text>
                </g>

                {/* 输出天线1按钮 */}
                <g style={{ cursor: antennaMode === 'manual' ? 'pointer' : 'not-allowed', opacity: antennaMode === 'manual' ? 1 : 0.6 }} onClick={() => {
                    if (antennaMode === 'manual') {
                        console.log('TopBarBottom - 点击输出天线1按钮');
                        console.log('TopBarBottom - 修改临时状态并下发指令');
                        // 修改临时状态
                        handleParameterChange({ outputAntenna: 1 });
                    }
                }}>
                    <line x1="1060" y1={35 + Y_OFFSET} x2="940" y2={35 + Y_OFFSET} stroke="#666666" strokeWidth="1" />
                    <rect x="1060" y={20 + Y_OFFSET} width="80" height="30" fill={amplifierData?.outputAntenna === 1 ? "green" : "#333333"} rx="4" ry="4" stroke="#555555" strokeWidth="1" />
                    <text x="1100" y={40 + Y_OFFSET} fill="white" fontSize="14" textAnchor="middle" dominantBaseline="middle">{t('输出天线1')}</text>
                </g>

                {/* 输出天线2按钮 */}
                <g style={{ cursor: antennaMode === 'manual' ? 'pointer' : 'not-allowed', opacity: antennaMode === 'manual' ? 1 : 0.6 }} onClick={() => {
                    if (antennaMode === 'manual') {
                        console.log('TopBarBottom - 点击输出天线2按钮');
                        console.log('TopBarBottom - 修改临时状态并下发指令');
                        // 修改临时状态
                        handleParameterChange({ outputAntenna: 2 });
                    }
                }}>
                    <line x1="1060" y1={100 + Y_OFFSET} x2="1000" y2={100 + Y_OFFSET} stroke="#666666" strokeWidth="1" />
                    <rect x="1060" y={85 + Y_OFFSET} width="80" height="30" fill={amplifierData?.outputAntenna === 2 ? "green" : "#333333"} rx="4" ry="4" stroke="#555555" strokeWidth="1" />
                    <text x="1100" y={105 + Y_OFFSET} fill="white" fontSize="14" textAnchor="middle" dominantBaseline="middle">{t('输出天线2')}</text>
                </g>

                {/* 输出天线3按钮 */}
                <g style={{ cursor: antennaMode === 'manual' ? 'pointer' : 'not-allowed', opacity: antennaMode === 'manual' ? 1 : 0.6 }} onClick={() => {
                    if (antennaMode === 'manual') {
                        console.log('TopBarBottom - 点击输出天线3按钮');
                        console.log('TopBarBottom - 修改临时状态并下发指令');
                        // 修改临时状态
                        handleParameterChange({ outputAntenna: 3 });
                    }
                }}>
                    <line x1="1060" y1={165 + Y_OFFSET} x2="1000" y2={165 + Y_OFFSET} stroke="#666666" strokeWidth="1" />
                    <rect x="1060" y={150 + Y_OFFSET} width="80" height="30" fill={amplifierData?.outputAntenna === 3 ? "green" : "#333333"} rx="4" ry="4" stroke="#555555" strokeWidth="1" />
                    <text x="1100" y={170 + Y_OFFSET} fill="white" fontSize="14" textAnchor="middle" dominantBaseline="middle">{t('输出天线3')}</text>
                </g>

                {/* 输出天线4按钮 */}
                <g style={{ cursor: antennaMode === 'manual' ? 'pointer' : 'not-allowed', opacity: antennaMode === 'manual' ? 1 : 0.6 }} onClick={() => {
                    if (antennaMode === 'manual') {
                        console.log('TopBarBottom - 点击输出天线4按钮');
                        console.log('TopBarBottom - 修改临时状态并下发指令');
                        // 修改临时状态
                        handleParameterChange({ outputAntenna: 4 });
                    }
                }}>
                    <line x1="1060" y1={230 + Y_OFFSET} x2="940" y2={230 + Y_OFFSET} stroke="#666666" strokeWidth="1" />
                    <rect x="1060" y={215 + Y_OFFSET} width="80" height="30" fill={amplifierData?.outputAntenna === 4 ? "green" : "#333333"} rx="4" ry="4" stroke="#555555" strokeWidth="1" />
                    <text x="1100" y={235 + Y_OFFSET} fill="white" fontSize="14" textAnchor="middle" dominantBaseline="middle">{t('输出天线4')}</text>
                </g>

            </svg>
        </div>
    );
};

export default TopBarBottom;